'use client'

import React, { useEffect, useRef, useState } from "react";
import LocationSection from "@/sections/LocationSection";
import FAQSection from "@/sections/FAQSection";
import { BranchSectionContent, LanguageType, NavbarSectionContent, TeamMemberSectionContent, LPResultsSectionContent, LPBenefitsSectionContent, LPServiceSectionContent, BeautyDeviceSectionContent } from "@/lib/types";
import { RIVIVE_LOCATION_LINK, FOUNDING_MEMBERSHIPS_TAKEN, FOUNDING_MEMBERSHIPS_TOTAL } from "@/lib/variables";
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
// import useBackgroundPosition from "../utils/useBackgroundPosition";
import NavbarContainer from "@/components/client/NavbarContainer";
import { PageData, FAQSectionContent } from "@/lib/types";
import { useLoading } from '@/contexts/LoadingContext';
import useAspectRatio from '@/hooks/useAspectRatio';
import LPHeroSection from "@/sections/LPHeroSection";
import LPFooterSection from "@/sections/LPFooterSection";
import LPAboutSection from "@/sections/LPAboutSection";
import LPDevicesSection from "@/sections/LPDevicesSection";
import LPServicesSection from "@/sections/LPServicesSection";
import LPImageSection from "@/sections/LPImageSection";
import LPBenefitsSection from "@/sections/LPBenefitsSection";
import UGCBenefitsSection from "@/sections/UGCBenefitsSection";
import LPResultsSection from "@/sections/LPResultsSection";
import LPImageFormSection from "@/sections/LPImageFormSection";
import LPCtaFormSection from "@/sections/LPCtaFormSection";
import TeamSection from "@/sections/TeamSection";
import SlidingFormSection from "@/components/SlidingFormSection/SlidingFormSection";

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);


// TODO: scrolls does not really reset from page to page.

// TODO: dont forget about will-change property

// TODO: dont forget about the image filters.

interface HomePageProps {
  pageData?: PageData;
  initialLanguage?: LanguageType;
}

// TODO: i think the preloader/page transition/animated navigation does not wait for the content fetch to finish atm.


const LandingPageClient: React.FC<HomePageProps> = ({
  // pageData
  initialLanguage
}) => {
  const language: LanguageType = initialLanguage ?? 'english';
  const searchParams = useSearchParams();
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();
  const isTb = useIsTb();
  const isLg = useIsLg();
  const pathname = usePathname();
  const router = useRouter();
  const shadowRef = useRef<HTMLDivElement>(null);
  const locationPositionRef = useRef<number | null>(null);
  const lpImagePositionRef = useRef<number | null>(null);
  const lpServicesPositionRef = useRef<number | null>(null);
  // debug.log('background position', backgroundPosition);

  // debug.log('scroll position', window.scrollY);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  const { contextSafe } = useGSAP({});

  // TODO: the color transition might get broken on resizes. check check check

  // Create a safe animation function that can be called anytime
  const animateBg = contextSafe(() => {

    // Kill only the color transition ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.id?.toString().startsWith('color-transition-')) {
        trigger.kill();
      }
    });
    if (lpImagePositionRef.current && lpServicesPositionRef.current && isLg) {

      const sections: {
        startY: number;
        fromColor: string;
        toColor: string;
      }[] = [];
      sections.push({
        startY: lpImagePositionRef.current,
        fromColor: '#FFF6E6', // TODO: use variables
        toColor: '#E8DACA',
      });
      sections.push({
        startY: lpServicesPositionRef.current,
        fromColor: '#E8DACA', // TODO: use variables
        toColor: '#FFF6E6', // TODO: use variables
      });

      sections.forEach((section, index) => {
        ScrollTrigger.create({
          start: `top+=${section.startY} center`,
          // markers: true,
          id: `color-transition-${index}`,
          onEnter: () => {
            gsap.to(document.body, {
              backgroundColor: section.toColor,
              duration: 1,
              ease: "power2.inOut"
            });
          },
          onLeaveBack: () => {
            gsap.to(document.body, {
              backgroundColor: section.fromColor,
              duration: 1,
              ease: "power2.inOut"
            });
          }
        });
      });
    }

  });

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);


  // debug.log("landing page page rerender");

  useEffect(() => {
    document.body.style.backgroundColor = '#FFF6E6';
    document.body.classList.add('will-change-bg');

  }, []);


  const [isSlidingFormOpen, setIsSlidingFormOpen] = useState(false);

  const handleOpenSlidingForm = () => {
    setIsSlidingFormOpen(true);
  };



  // Members whose photo is still the "no image" placeholder are hidden on the LP
  const PLACEHOLDER_TEAM_IMAGE = 'About_Team_NA_';

  const teamMemberSectionPageData: TeamMemberSectionContent = {
    teamMembers: (language === 'arabic' ? [
      {
        teamMemberName: 'جورجينا اندور',
        teamMemberPosition: 'مديرة منطقة',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Georgiana_V2_171b0a3d5e.webp'
        },
        teamMemberImageAltText: 'Georgiana Andor',
        displayOrder: 1,
      },
      {
        teamMemberName: 'دينيسا فالنتينا شيبوش',
        teamMemberPosition: 'مستشارة الجمال',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Denisa_V2_71f32edcd8.webp'
        },
        teamMemberImageAltText: 'Denisa Valentina Șipoș',
        displayOrder: 2,
      },
      {
        teamMemberName: 'تمارا مسوح',
        teamMemberPosition: 'اخصائية تجميل',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_NA_30b216000d.webp'
        },
        teamMemberImageAltText: 'Tamara Massouh',
        displayOrder: 3,
      },
      {
        teamMemberName: 'انستازيا شوروشنيوك',
        teamMemberPosition: 'معالجة تجميل',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Anastasia_V2_f52170bb39.webp'
        },
        teamMemberImageAltText: 'Anastasia Ciurusniuc',
        displayOrder: 4,
      },
      {
        teamMemberName: 'ليندا خليل',
        teamMemberPosition: 'معالجة تجميل',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Linda_63d62768e4.webp'
        },
        teamMemberImageAltText: 'Linda Khalil',
        displayOrder: 5,
      },
      {
        teamMemberName: 'بيبي سارا أوفيزوفا',
        teamMemberPosition: 'منسقة استقبال',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Bibisara_V2_cd4e4c7678.webp'
        },
        teamMemberImageAltText: 'Bibisara Ovezova',
        displayOrder: 6,
      },
      {
        teamMemberName: 'هبه جمال',
        teamMemberPosition: 'الاخصائية للجمال',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Hebah_V2_004cb19e81.webp'
        },
        teamMemberImageAltText: 'Hebah Jamal',
        displayOrder: 7,
      },
      {
        teamMemberName: 'ملاك خالد',
        teamMemberPosition: 'مستشار الجمال',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Malak_V2_633b5a6c3c.webp'
        },
        teamMemberImageAltText: 'Malak Khaled',
        displayOrder: 8,
      },
      {
        teamMemberName: 'اثير محمد',
        teamMemberPosition: 'مركز خدمة العملاء',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_NA_30b216000d.webp'
        },
        teamMemberImageAltText: 'Atheer Mohammed',
        displayOrder: 9,
      },
    ] : [
      {
        teamMemberName: 'Georgiana Andor',
        teamMemberPosition: 'Branch Manager',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Georgiana_V2_171b0a3d5e.webp'
        },
        teamMemberImageAltText: 'Georgiana Andor',
        displayOrder: 1,
      },
      {
        teamMemberName: 'Denisa Valentina Șipoș',
        teamMemberPosition: 'Beauty Advisor',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Denisa_V2_71f32edcd8.webp'
        },
        teamMemberImageAltText: 'Denisa Valentina Șipoș',
        displayOrder: 2,
      },
      {
        teamMemberName: 'Tamara Massouh',
        teamMemberPosition: 'Beauty Specialist',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_NA_30b216000d.webp'
        },
        teamMemberImageAltText: 'Tamara Massouh',
        displayOrder: 3,
      },
      {
        teamMemberName: 'Anastasia Ciurusniuc',
        teamMemberPosition: 'Beauty Therapist',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Anastasia_V2_f52170bb39.webp'
        },
        teamMemberImageAltText: 'Anastasia Ciurusniuc',
        displayOrder: 4,
      },
      {
        teamMemberName: 'Linda Khalil',
        teamMemberPosition: 'Beauty Therapist',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Linda_63d62768e4.webp'
        },
        teamMemberImageAltText: 'Linda Khalil',
        displayOrder: 5,
      },
      {
        teamMemberName: 'Bibisara Ovezova',
        teamMemberPosition: 'Front Desk Coordinator',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Bibisara_V2_cd4e4c7678.webp'
        },
        teamMemberImageAltText: 'Bibisara Ovezova',
        displayOrder: 6,
      },
      {
        teamMemberName: 'Hebah Jamal',
        teamMemberPosition: 'Beauty Specialist',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Hebah_V2_004cb19e81.webp'
        },
        teamMemberImageAltText: 'Hebah Jamal',
        displayOrder: 7,
      },
      {
        teamMemberName: 'Malak Khaled',
        teamMemberPosition: 'Beauty Advisor',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_Malak_V2_633b5a6c3c.webp'
        },
        teamMemberImageAltText: 'Malak Khaled',
        displayOrder: 8,
      },
      {
        teamMemberName: 'Atheer Mohammed',
        teamMemberPosition: 'Call Center Coordinator',
        teamMemberImage: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Team_NA_30b216000d.webp'
        },
        teamMemberImageAltText: 'Atheer Mohammed',
        displayOrder: 9,
      },
    ]).filter(member => !member.teamMemberImage.url.includes(PLACEHOLDER_TEAM_IMAGE))
  }
  // TODO: retest this. maybe related to weird scroll position on page change?
  useEffect(() => {
    setTimeout(() => {
      const scrollToFAQ = searchParams.get('scrollToFAQ');
      if (scrollToFAQ === 'true') {
        // debug.log('scroll to faq');
        document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); // TODO: this does not scroll correctly atm. probably due to the gsap pin animations and aspect ratios.
        router.push(pathname); // Remove query param
      } else {
        // debug.log("no scroll to faq");
      }
    }, 200);
  }, [searchParams, pathname, router]);

  // if (!pageData) {
  //   debug.log('--------------------------------');
  //   debug.log('PAGE DATA IS NULL');
  //   debug.log('--------------------------------');
  //   return null; // TODO: Or a loading spinner
  // }


  const navbarSectionPageData: NavbarSectionContent = {
    promoBarText: 'Promo Bar Text',
    promoBarVisibility: true
  }

  const branchInfoSectionPageData: BranchSectionContent = {
    branches: [
      {
        branchName: language === 'arabic' ? 'ريڤيڤ' : 'Rivive',
        image1: {
          url: '/3_Landing-Page/Landing-10-Location/Index-Location-1.webp'
        },
        image1AltText: 'Rivive Location Image 1',
        image2: {
          url: '/3_Landing-Page/Landing-10-Location/Index-Location-2.webp'
        },
        image2AltText: 'Rivive Location Image 2',
        openingHoursSatToWed: language === 'arabic' ? '8 ص – 10 م' : '8 AM – 10 PM',
        openingHoursThu: language === 'arabic' ? '8 ص – 8 م' : '8 AM – 8 PM',
        openingHoursFri: language === 'arabic' ? '10 ص – 6:30 م' : '10 AM – 6:30 PM',
        branchAddress: language === 'arabic'
          ? 'لومير سنتر، حي حطين\nRRHA8736، الرياض، السعودية'
          : 'Lumière Center, Hittin\nRRHA8736, Riyadh, SA',
        branchAddressLink: RIVIVE_LOCATION_LINK,
        timetableHeading: language === 'arabic' ? "ساعات العمل" : "Opening Hours",
        locationHeading: language === 'arabic' ? "كيفية الوصول إلينا" : "How to find us",
        buttonText: language === 'arabic' ? "انضم إلى ريڤيڤ" : "Join Rivive",
        satWedLabel: language === 'arabic' ? "السبت - الأربعاء" : "Sat – Wed",
        thursdayLabel: language === 'arabic' ? "الخميس" : "Thu",
        fridayLabel: language === 'arabic' ? "الجمعة" : "Fri",
        googleMapsButtonText: language === 'arabic' ? "اعثر علينا على خرائط جوجل" : "Find us on Google Maps"
      }
    ]
  }
  const faqSectionPageData: FAQSectionContent = {
    questions: language === 'arabic' ? [
      {
        question: 'متى ستلاحظون النتائج؟',
        answer: 'يلاحظ العديد من العملاء تحسنًا فوريًا في ملمس البشرة، تقليل السيلوليت، نحت الجسم، أو إشراقة عامة مباشرة بعد جلستهم الأولى.\n\nوبما أن علاجاتنا تحفّز العمليات الطبيعية في جسمكم بدلًا من التغييرات الاصطناعية، فإن أبرز التحولات عادةً ما تتطور خلال 2-4 أسابيع، مما يسمح لبشرتكم وجسمكم بالتجدد بشكل طبيعي وصحي.'
      },
      {
        question: 'ماذا لو لم تلاحظون أي نتائج أو لم تكونوا راضين عن النتيجة؟',
        answer: 'نستخدم أجهزة عالية الأداء ومختبرة سريريًا تقدم نتائج حقيقية وملموسة باستمرار. تم اختيار تقنياتنا بعناية والتحقق من فعاليتها وسلامتها، فهي ليست مجرد رائدة في الصناعة، بل مثبتة النتائج. وبما أن استجابة كل جسم مختلفة، فإن نهجنا دائمًا شخصي.\n\nإذا لم تلاحظوا التحسينات المتوقعة، سنعيد تقييم بروتوكولكم ونعدّله باستخدام البيانات اللحظية وتعليقاتكم. أولويتنا هي إيجاد أنسب مسار علاجي لاحتياجاتكم الفريدة.'
      },
      {
        question: 'هل لديكم أي دليل على فعالية علاجاتكم؟',
        answer: 'بالتأكيد. نستخدم تقنيات حاصلة على براءات اختراع ومعتمدة (Tesla Med، Hitek) تم اختبارها سريريًا والتحقق من فعاليتها من خلال الأبحاث العلمية. هذه الأنظمة المتقدمة وغير الجراحية مدعومة بنتائج موثقة في تقليل السيلوليت، نحت الجسم، علاج حب الشباب، وتجديد البشرة.\n\nلقد خضعت كل تقنية لاختبارات صارمة على أنواع البشرة المختلفة ومناطق الجسم المتنوعة، لضمان السلامة والفعالية المستمرة. علاجاتنا محل ثقة المهنيين حول العالم، وكل بروتوكول يعتمد على العلم المثبت، لتشعروا بالثقة بأنكم تتلقون رعاية قائمة على النتائج الفعلية.'
      },
      {
        question: 'لماذا يجب أن أصدق أن تقنياتكم أفضل فعليًا من ما تقدمه أماكن أخرى؟',
        answer: 'تتميز ريڤيڤ بشراكات حصرية عالميًا في مجال التقنية الجمالية (Tesla Med، Hitek)، مقدمة أجهزة متطورة تجمع بين الفخامة والنتائج الملموسة، وغير متاحة في أي مكان آخر في المملكة العربية السعودية.\n\nتم اختيار كل جهاز ليس لمجرد الابتكار، بل لقدرته على تقديم نتائج دقيقة، آمنة، ومريحة بطريقة غير جراحية باستخدام تقنية البيوفيدباك. نهجنا مخصص بالكامل، يجمع بين التكنولوجيا المتقدمة والصيغ المستهدفة لإنشاء خطط علاجية فعّالة للغاية ومصممة خصيصًا لتلبية احتياجاتك وأهدافك الفردية.'
      },
      {
        question: 'كيف يمكنني التأكد أن علاجاتكم مخصصة بالفعل وليست عامة لكل العملاء؟',
        answer: 'علاجاتنا لا تعتمد أبدًا على نهج واحد يناسب الجميع. قبل إعداد أي خطة، يخضع كل عميل لاستشارة شاملة تتضمن تحليلًا متقدمًا باستخدام أجهزة التشخيص عالية المستوى لتقييم الوجه والجسم.\n\nخلال هذه الاستشارة، نقيم ليس فقط حالة بشرتك أو جسمك، بل أيضًا نمط حياتك، تاريخك الصحي، وأهدافك. يسمح لنا هذا النهج التفصيلي بإنشاء خطة علاجية شخصية بالكامل مصممة لمعالجة الأسباب الجذرية لكل مشكلة, وليس مجرد الأعراض الظاهرة.\n\nيتم متابعة كل عميل عن كثب طوال العملية، ويتم تعديل العلاجات بناءً على التقدم الفردي، لضمان نتائج طويلة الأمد ودعم مستمر.'
      },
      {
        question: 'هل العلاجات مؤلمة، محفوفة بالمخاطر أو جراحية؟',
        answer: 'علاجات ريڤيڤ آمنة تمامًا وغير جراحية، وتُجرى باستخدام تقنيات متقدمة ومعتمدة على يد متخصصين مدربين مهنيًا. توفر نتائج سريعة وفعّالة وملموسة دون ألم أو فترة تعافي أو أي مخاطر، من خلال تحفيز عمليات التجدد الطبيعية في جسمك لاستعادة التوازن والحيوية.\n\nالآثار الجانبية قليلة وطبيعية، مثل احمرار بسيط، كدمات خفيفة، أو توتر عضلي طفيف. هذه العلامات تدل ببساطة على أن جسمك يستجيب بشكل إيجابي للعلاج.'
      },
      {
        question: 'هل تعمل علاجاتكم على جميع أنواع البشرة؟',
        answer: 'نعم، تقنيتنا المتقدمة للبيوفيدباك مصممة للعمل بأمان وفعالية على جميع أنواع وألوان البشرة. قبل بدء أي علاج، تقوم أجهزتنا بتحليل خصائص بشرتك الفريدة , بما في ذلك مستويات الحساسية، التصبغ، الملمس، والمشاكل المحددة, ثم تقوم بالمعايرة تلقائيًا وفق احتياجاتك الفردية.\n\nيضمن هذا النهج الشخصي تحقيق أفضل النتائج سواء كانت بشرتك حساسة، دهنية، جافة، مختلطة، أو ناضجة. خبراؤنا العالميون مدربون تدريبًا واسعًا للعمل مع جميع أنواع البشرة، وبما أن كل بروتوكول يعتمد على البيانات ومخصص خصيصًا لك، فإن علاجك ملائم تمامًا لاحتياجات بشرتك منذ اليوم الأول.'
      },
      {
        question: 'ما هي الحالات الطبية التي قد تمنعني من الحصول على العلاجات؟',
        answer: 'في ريڤيڤ، سلامتكم هي أولويتنا القصوى. نتبع بروتوكولات طبية صارمة ونراجع بعناية أي حالات صحية موجودة قبل بدء رحلتك العلاجية. إذا وُجدت أي موانع، سنعدّل خطة العلاج لضمان أن تظل آمنة تمامًا ومخصصة لاحتياجاتك.\n\nومع ذلك، بالنسبة للحالات الطبية الخطيرة — مثل السرطان النشط، اضطرابات القلب الحادة، أو أمراض المناعة الذاتية غير المسيطر عليها, لا يمكن إجراء العلاجات. صحتك دائمًا هي الأولوية، و سنرشدك بصدق واهتمام في كل خطوة.'
      },
      {
        question: 'لماذا يجب أن أدفع أسعارًا مميزة بينما توجد خيارات أرخص؟',
        answer: 'ريڤيڤ متميزة، حيث تقدم علاجات فاخرة، غير جراحية، ومدعومة علميًا من خلال شراكات حصرية مع مزودين دوليين , متاحة فقط في ريڤيڤ بالمملكة العربية السعودية وبعض المواقع العالمية المختارة. مع فريقنا الخبير، الرعاية الشخصية، والنتائج الاستثنائية دون ألم أو فترة تعافي، نقدم تجربة فاخرة مصممة لأولئك الذين يطلبون أعلى معايير الجمال والرفاهية.'
      },
      {
        question: 'ما هي سياستكم بشأن تأكيد الحجوزات, الإلغاء, وإعادة الجدولة؟',
        answer: 'تأكيد المواعيد\n\nيجب تأكيد جميع الحجوزات قبل 24 ساعة على الأقل من موعد الجلسة.\n\n سيتم إلغاء المواعيد غير المؤكدة تلقائيًا قبل 12 ساعة من الموعد، وسيُعرض الحجز على العملاء المدرجين في قائمة الانتظار.\n\nالإلغاءات وإعادة الجدولة\n\nيمكن إلغاء المواعيد أو إعادة جدولتها دون أي غرامة إذا تم ذلك قبل 12 ساعة على الأقل من موعد الجلسة.\n\nالإلغاءات المتأخرة وعدم الحضور\n\nيُعتبر إلغاء الموعد خلال فترة تقل عن 12 ساعة قبل الموعد أو عدم الحضور إلغاءً متأخرًا.\n\n يحصل كل عميل على ثلاث فرص لكل دورة عضوية.\n بعد استنفاد هذه الفرص، سيتم خصم الجلسات الفائتة من الباقة.\n\nتوفر إعادة الجدولة\n\nسيتم تحديد المواعيد التي تم إعادة جدولتها وفقًا لأقرب وقت متاح، وقد يختلف الوقت حسب مدى توفر المواعيد في المركز.'
      }
    ] : [
      {
        question: 'How soon will I see results?',
        answer: 'Many clients notice immediate improvements in skin texture, cellulite reduction, body contouring, or overall radiance right after their first session.\n\nSince our treatments stimulate your body\'s natural processes rather than creating artificial changes, the most dramatic transformations typically develop over 2-4 weeks. This allows your skin and body to regenerate naturally and healthily.'
      },
      {
        question: 'What if I don\'t see any results or I\'m not satisfied with the outcome?',
        answer: 'We use top-performing, clinically tested devices that consistently deliver real, visible results. Our technologies are carefully selected and validated to ensure both safety and effectiveness, they\'re not just industry-leading, they\'re results-proven.\n\nSince every body responds differently, our approach is always personalized. If you\'re not seeing the improvements you expected, we\'ll reassess and adjust your protocol using real-time data and your feedback. Our priority is finding the most effective treatment path for your unique needs.'
      },
      {
        question: 'Do you have any proof that your treatments work?',
        answer: 'Absolutely. We use patented and certified technologies (Tesla Med, Hitek) that have been clinically tested and validated through scientific research. These advanced, non-invasive systems are backed by documented results in cellulite reduction, body contouring, acne treatment, and skin regeneration.\n\nEach technology has undergone rigorous testing across various skin types and body areas, ensuring both safety and consistent effectiveness. Our treatments are trusted by professionals worldwide, and every protocol is built on proven science, so you can feel confident you\'re receiving results-driven care.'
      },
      {
        question: 'Why should I believe your technology is actually better than what other places offer?',
        answer: 'Rivive is powered by exclusive global beauty-tech partnerships (Tesla Med, Hitek), offering cutting-edge devices that are both luxurious and results-driven, unavailable anywhere else in Saudi Arabia. Each machine has been selected not just for innovation, but for its ability to deliver precise, safe, and comfortable non-invasive results using biofeedback technology.\n\nOur approach is fully customized, combining advanced technology with targeted formulas to create treatment plans that are both highly effective and uniquely tailored to your individual needs and goals.'
      },
      {
        question: 'How can I know if your treatments are actually personalized and not generic for every client?',
        answer: 'Our treatments are never one-size-fits-all. Before any plan is created, every client undergoes a comprehensive consultation that includes advanced analysis using our high-end diagnostic devices for facial and body assessment.\n\nDuring this consultation, we evaluate not just your skin or body condition, but also your lifestyle, health history, and goals. This detailed approach allows us to create a fully personalized treatment plan designed to address the root causes of each concern—not just the visible symptoms.\n\nEach client is closely monitored throughout the process, and treatments are adjusted based on individual progress, ensuring long-term results and continuous support.'
      },
      {
        question: 'Are the treatments painful, risky or invasive?',
        answer: 'Rivive treatments are 100% safe and non-invasive, performed using advanced, certified technology by professionally trained specialists. They deliver fast, effective, and visible results without pain, downtime, or risk by stimulating your body\'s natural regenerative processes to restore balance and vitality.\n\nSide effects are minimal and natural—such as slight redness, mild bruising, or light muscle soreness. These are simply signs that your body is activating and responding positively to the treatment.'
      },
      {
        question: 'Do your treatments work on any skin type?',
        answer: 'Yes, our advanced biofeedback technology is designed to work safely and effectively on all skin types and tones.\n\nBefore any treatment begins, our devices analyze your unique skin characteristics - including sensitivity levels, pigmentation, texture, and specific concerns - then automatically calibrate to your individual needs.\n\nThis personalized approach ensures optimal results whether you have sensitive, oily, dry, combination, or mature skin.\n\nOur global specialists are extensively trained to work with diverse skin types, and because every protocol is data-driven and customized, your treatment is perfectly suited to your skin\'s unique requirements from day one.'
      },
      {
        question: 'What medical conditions can prevent me from getting the treatments?',
        answer: 'At Rivive, your safety is our top priority. We follow strict medical protocols and carefully review any existing health conditions before starting your treatment journey. If any contraindications are present, we\'ll adjust your treatment plan to ensure it remains 100% safe and personalized to your needs.\n\nHowever, for serious medical conditions—such as active cancer, severe cardiovascular disorders, or uncontrolled autoimmune diseases—treatments cannot be performed. Your health always comes first, and we\'ll guide you with honesty and care every step of the way.'
      },
      {
        question: 'Why should I pay premium prices when there are cheaper options available?',
        answer: 'Rivive is unique, offering luxury, non-invasive, science-backed treatments through exclusive partnerships with international providers—available only at Rivive in Saudi Arabia and select locations worldwide.\n\nCombined with our expert team, personalized care, and exceptional results without pain or downtime, we deliver a premium experience designed for those who demand the highest standards in beauty and wellness.'
      },
      {
        question: 'What is your policy regarding booking confirmations, cancellations, and rescheduling?',
        answer: 'Confirmations\n\nAll bookings must be confirmed at least 24 hours prior to the appointment time.\n\nUnconfirmed slots will be canceled automatically 12 hours before the appointment time and offered to clients on our waitlist.\n\nCancellations & Rescheduling\n\nAppointments may be canceled or rescheduled without penalty up to 12 hours in advance.\n\nLate Cancellations & No-Shows\n\nCanceling an appointment with less than 12 hours\' notice or missing the appointment counts as a late cancellation.\n\nEach client receives three grace allowances per membership cycle. After these allowances are exhausted, missed sessions will be deducted from the package.\n\nRescheduling Availability\n\nRescheduled appointments will be assigned to the next available time slot, which may vary depending on the availability of the center.'
      }
    ]
  }
  // const brandInfoSectionPageData: BrandInfoSectionContent = {
  //   instagramLink: '',
  //   tiktokLink: '',
  //   facebookLink: '',
  //   linkedinLink: '',
  //   mainTelephoneNumber: '',
  //   mainEmailAddress: ''
  // }

  const beautyDevicesSectionPageData: BeautyDeviceSectionContent = {
    beautyDevices: language === 'arabic' ? [
      {
        deviceName: 'تسلا-ميد',
        deviceDescription: 'تعمل تسلا مِد على تحويل ملامح وجهك، ونحت جسمك، وإذابة الدهون باستخدام تقنية الأيونات المتطورة.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Tesla-2.webp'
        },
        deviceImageAltText: 'TeslaMed Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 1
      },

      {
        deviceName: 'هايتك توربيدو',
        deviceDescription: 'جهاز هاي-تيك توربيدو يعيد صياغة قواعد العناية بالبشرة، مستخدمًا تقنية متقدمة لشدّ بشرتك وتنعيمها وتحسين ملامح الوجه.',
        deviceImage: {
          // url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Torpedo.webp'
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Torpedo_1.webp'
          // url: '/3_Landing-Page/Landing-4-Services/Landing-Services-Aging.webp'
        },
        deviceImageAltText: 'Hitek Torpedo Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 3
      },
      {
        deviceName: 'هايتك بريسوماكس',
        deviceDescription: 'يُدمج جهاز هاي-تيك بريسومكس بين الابتكار والصحة، مستخدمًا تقنية التصريف اللمفاوي الذكية لتنشيط جسمك، وشدّه، وتنقيته.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Presomax.webp'
        },
        deviceImageAltText: 'Hitek Pressomax Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 4

      },
      {
        deviceName: 'ريڤيڤ رادينس برو',
        deviceDescription: 'يقدم جهاز ريڤيڤ RP علاجات مستهدفة وغير مؤلمة تعمل على إعادة تشكيل البشرة، وشدها، وتنشيطها. إنه يضع معيارًا جديدًا في العلاجات التجميلية الاحترافية.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Revive.webp'
        },
        deviceImageAltText: 'Revive Radiance Pro Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 2
      }
    ] : [
      {
        deviceName: 'TESLA MED',
        deviceDescription: 'Teslamed transforms your face, sculps your body & melts fat with cutting-edge Ionic Technology.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Tesla-2.webp'
        },
        deviceImageAltText: 'TeslaMed Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 1
      },

      {
        deviceName: 'HITEK TORPEDO',
        deviceDescription: 'Hitek Torpedo reshapes the rules of skincare, using advanced technology to lift, refine, and tighten your facial skin.',
        deviceImage: {
          // url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Torpedo.webp'
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Torpedo_1.webp'
          // url: '/3_Landing-Page/Landing-4-Services/Landing-Services-Aging.webp'
        },
        deviceImageAltText: 'Hitek Torpedo Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 3
      },
      {
        deviceName: 'HITEK PRESSOMAX',
        deviceDescription: 'Hitek Pressomax blends innovation and wellness, using smart lymphatic drainage to revitalize, tone, and purify your body.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Presomax.webp'
        },
        deviceImageAltText: 'Hitek Pressomax Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 4
      },
      {
        deviceName: 'Revive Radiance Pro',
        deviceDescription: 'REVIVE RP delivers targeted, painless treatments that reshape, firm, and revitalize. It sets a new standard in professional cosmetic treatments.',
        deviceImage: {
          url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Revive.webp'
        },
        deviceImageAltText: 'Revive Radiance Pro Device',
        deviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        },
        deviceOrder: 2
      }
    ]
  }

  const servicesSectionPageData: LPServiceSectionContent = {

    services: language === 'arabic' ? [
      {
        serviceName: 'التجاعيد وعلامات التقدم في العمر',
        serviceDescription: 'قلّلوا من ظهور علامات التقدم في العمر والندوب والتصبغات، مع تعزيز إشراقة بشرتكم الطبيعية',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Wrinkles.webp'
        },
        serviceImageAltText: 'Initial Analysis Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },

      {
        serviceName: 'دهون الذراعين والبطن',
        serviceDescription: 'تخلصوا من الدهون المتراكمة في الذراعين والبطن وساعدوا على تنسيق قوام جسمكم بالكامل',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Arms.webp'
        },
        serviceImageAltText: 'Lifting and Tightening Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'علامات التمدد والندوب والسيلوليت',
        serviceDescription: 'قلّلوا من السيلوليت وعلامات التمدد والندوب، مع تحفيز إنتاج الكولاجين للحصول على بشرة أكثر تماسكًا وإشراقًا',
        serviceImage: {
          // url: '/3_Landing-Page/Landing-4-Services/Landing-Services-Aging.webp'
          url: '/VER_2026/Landing-Services-Cellulite.webp'
          // url: '/3_Landing-Page/Landing-5-Devices/Landing-Devices-Torpedo.webp'
        },
        serviceImageAltText: 'Anti-Aging Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },

      {
        serviceName: 'فقدان الوزن وتنسيق القوام',
        serviceDescription: 'التخلص من الخلايا الدهنية تدريجيًا من خلال العمليات الطبيعية للجسم مع المساعدة في نحت وتنسيق القوام للوصول للشكل المطلوب',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Lose.webp'
        },
        serviceImageAltText: 'Body Reshaping Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'عيوب البشرة و اللغد',
        serviceDescription: 'المساعدة في تحسين عيوب البشرة بالوجه والتقليل من اللغد بشكل طبيعي.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Skin.webp'
        },
        serviceImageAltText: 'Cellulite Treatment Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'امتلاء الشفاه وإشراقة الوجه',
        serviceDescription: 'شدّ البشرة ورفعها وتحسين تحديد ملامح الوجه ومنطقة العينين للحصول على مظهر أكثر إشراقًا ونضارة',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Lip.webp'
        },
        serviceImageAltText: 'Healing and Recovery Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      }
    ] : [
      {
        serviceName: 'Wrinkles & aging signs',
        serviceDescription: 'Reduce visible signs of aging, scars and pigmentation while enhancing your skin\'s natural glow.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Wrinkles.webp'
        },
        serviceImageAltText: 'Initial Analysis Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },

      {
        serviceName: 'Arms & belly fat',
        serviceDescription: 'Reduce the fat on the arms & belly and reshape your whole body.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Arms.webp'
        },
        serviceImageAltText: 'Lifting and Tightening Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'Stretch marks, scars & cellulite',
        serviceDescription: 'Eliminate your cellulite, stretch marks & scars while boosting collagen production for firmer, more radiant skin texture.',
        serviceImage: {
          // url: '/3_Landing-Page/Landing-4-Services/Landing-Services-Aging.webp'
          url: '/VER_2026/Landing-Services-Cellulite.webp'
        },
        serviceImageAltText: 'Anti-Aging Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },

      {
        serviceName: 'Lose weight & reshape your body',
        serviceDescription: 'Melt away fat cells through natural processes while sculpting your ideal silhouette.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Lose.webp'
        },
        serviceImageAltText: 'Body Reshaping Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'Skin imperfections & double chin',
        serviceDescription: 'Naturally heal the imperfections on your face & get rid of the double chin.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Skin.webp'
        },
        serviceImageAltText: 'Cellulite Treatment Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      },


      {
        serviceName: 'Lip plumping & glowing face',
        serviceDescription: 'Lift, tighten & define the skin on your face and eyes for a glowy appearance.',
        serviceImage: {
          url: '/VER_2026/Landing-Services-Lip.webp'
        },
        serviceImageAltText: 'Healing and Recovery Service',
        serviceLogo: {
          url: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/hero_image_07f95a3b19.avif'
        }
      }
    ]
  }

  const benefitsSectionPageData: LPBenefitsSectionContent = {

    values: language === 'arabic' ? [
      {
        heading: 'نتائج فورية من الجلسة الأولى',
        paragraph: '"كنت بالبداية أحسب إن العلاجات غير الجراحية ما تعطي نتيجة قوية، بس بعد كم جلسة تفاجأت من النتائج بدون أي ألم."',
        author: 'ساره ف.'
      },
      {
        heading: 'شخصي، خالٍ من الألم وغير جراحي',
        paragraph: '"توقعت أحتاج فترة تعافي واجازه من الدوام، بس طلع الموضوع أسهل من السهل, رحت عالعشا وأنا شكلي وإحساسي مرّة حلو."',
        author: 'نهى م.'
      },
      {
        heading: 'سهل وبدون أي فترة تعافي',
        paragraph: '"كنت بصراحة شاكة بموضوع إن كل شيء يكون مُفصَّل لي، بس تفاجأت إن ريڤيڤ سوّوا ثلاث تحاليل وصمّموا البرنامج كامل لي أنا"',
        author: 'لينا س.'
      }
    ] : [
      {
        heading: 'Immediate results from the first session',
        paragraph: 'At first I thought non-invasive meant less effective, but after a few sessions I was amazed I could get such incredible results without any discomfort.',
        author: 'Sarah F.'
      },
      {
        heading: 'Personalized, pain-free & non-invasive',
        paragraph: 'I was planning to take time off work after the treatment, but I actually never required any recovery. I went straight to dinner looking and feeling amazing.',
        author: 'Nuha M.'
      },
      {
        heading: 'Effortless & zero recovery time',
        paragraph: 'I was skeptical about the \'personalized\' claims, but Rivive actually did three analyses and designed everything specifically for me.',
        author: 'Leena S.'
      }
    ]
  }

  const lpResultsSectionPageData: LPResultsSectionContent = {

    results: language === 'arabic' ? [
      {
        resultDescription: '"مع مرور الوقت، بشرة ذراعي صارت مره سيئة. الصراحة انصدمت إني قدرت أحسنها بدون عمليات. ,ودي اعرف إذا ينفع بعد لباقي مناطق الجسم."',
        resultAuthor: 'لمى ن.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/1-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 1',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/1-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 1',
        actionText: 'بعد اربع جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"كانت جفوني ثقيلة ومرتخية لدرجة إني دايم أبان تعبانة. كنت مره فاقدة الثقة بنفسي لدرجة إني بطلت أتصور تمامًا. الحين عيوني صارت مرفوعة، مفتوحة، وشكلها أصغر من قبل!"',
        resultAuthor: 'هند س.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/2-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 2',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/2-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 2',
        actionText: 'بعد ثلاث جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"على طول بعد الجلسة، حسّيت إن بشرتي صارت مشدودة أكثر. الخطوط الخفيفة حول عيوني خفّت بشكل واضح. فعليًا أحس إني رجعت بالزمن!"',
        resultAuthor: 'نورا م.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/3-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 3',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/3-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 3',
        actionText: 'بعد اربع جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"الجلد المجعد في رقبتي وصدري كان دايم يفضح عمري، حتى لو وجهي شكله كويس. بعد كم جلسة، بشرتي صارت مشدودة وناعمة من جديد. التغيير فعلاً صادم!"',
        resultAuthor: 'لين ص.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/4-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 4',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/4-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 4',
        actionText: 'بعد 6 جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"ما كنت أصدق إن ذراعي ممكن ترجع مشدودة كذا بدون عملية. الجلد المترهل اللي كان يخليني أتحسس من شكلي اختفى تمامًا."',
        resultAuthor: 'ياسمين أ.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/5-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 5',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/5-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 5',
        actionText: 'بعد 6 جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"كنت أحس بعدم راحة بجسمي لدرجة قررت أجرب الباقة الكاملة من ريڤيڤ، والحين جسمي كله صار كأنه جديد تمامًا. فعلاً عندهم تقنية ثورية هنا"',
        resultAuthor: 'دينا م.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/6-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 6',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/6-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 6',
        actionText: 'بعد 10 جلسات من تسلا ميد',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      }
    ] : [
      {
        resultDescription: '"Over the years the skin on my arms started to look very bad. I\'m surprised that I was able to fix it without surgery. I wonder if this also works on other body parts."',
        resultAuthor: 'Lama N.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/1-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 1',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/1-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 1',
        actionText: 'After 4 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"My eyelids were so heavy and droopy that I always looked tired. I was so insecure that I started avoiding photos completely. Now my eyes look lifted, open, and youthful again!"',
        resultAuthor: 'Hind S.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/2-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 2',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/2-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 2',
        actionText: 'After 3 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"Immediately after the treatment my skin felt firmer. The fine lines around my eyes softened noticeably. I genuinely feel like I\'ve turned back time."',
        resultAuthor: 'Nora M.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/3-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 3',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/3-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 3',
        actionText: 'After 4 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"The wrinkled skin on my neck and chest always gave away my age, no matter how good my face looked. After a few sessions, my skin became tight and smooth again. The transformation is shocking."',
        resultAuthor: 'Leen S.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/4-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 4',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/4-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 4',
        actionText: 'After 6 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"I couldn\'t believe my arms could look this firm again without surgery. The sagging skin that made me so self-conscious is completely gone."',
        resultAuthor: 'Yasmeen A.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/5-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 5',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/5-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 5',
        actionText: 'After 6 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      },
      {
        resultDescription: '"I felt so uncomfortable in my own body that I decided to try the full package from Rivive, and now my whole body looks brand new. They really have revolutionary technology here."',
        resultAuthor: 'Deena M.',
        resultImage1Url: '/3_Landing-Page/Landing-2-Results/6-Results-Before.webp',
        resultImage1AltText: 'Before Treatment Result 6',
        resultImage2Url: '/3_Landing-Page/Landing-2-Results/6-Results-After.webp',
        resultImage2AltText: 'After Treatment Result 6',
        actionText: 'After 10 sessions with Tesla Med',
        deviceImageUrl: '/3_Landing-Page/Landing-2-Results/Landing-Results-Devices-Disclaimer-Tesla.webp',
        deviceImageAltText: 'Tesla device'
      }
    ]
  }

  return (
    <div
      className="flex flex-col items-center section-gaps relative">

      <LPHeroSection
        id="hero"
        title={language === 'arabic' ? "أول مركز تجميل غير جراحي في الرياض" : "Riyadh’s first non-invasive beauty center"}
        paragraph={language === 'arabic' ? "مصمم للذين يقدّرون الخصوصية والرفاهية والنتائج دون أي تنازل." : "Designed for those who value privacy, luxury, and results without compromise."}
        hint="*20% Discount for Online Sign-ups"
        buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
        imageSrc="/3_Landing-Page/Landing-1-Hero/Landing-Hero.webp"
        imageAlt="Rivive Hero"
        // mediaType="video"
        videoSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/sample_video_dba0075496.mp4"
        useMuxPlayer={true}
        muxPlaybackIdDesktop="vZOqaovQI5BZlFH32gq005uTXadxpLOuvPrfmIM3ehH4"
        muxPlaybackIdMobile="RXEoHDE025FUL02nnD922ihtRPxWoQrILlu00SYC02ybiXw"
        language={language}
        titleFont="small"

        benefits={language === 'arabic' ? [
          "بدون إبر أو جراحة",
          "بدون الم",
          "بدون فترة تعافي"
        ] : [
          "Without needles or surgery",
          "Without pain",
          "Without recovery time"
        ]}
        testimonials={language === 'arabic' ? [
          {
            quote: '"ما خطر ببالي إن الجمال ممكن يجي بهالعمق, ويرجع لي راحتي بهالشكل"',
            author: 'ندى ف.'
          },
          {
            quote: '"وجهي اختلف بشكل كبير, وصرت لأول مرة من زمان واثقة بدون مكياج"',
            author: 'هيفاء س.'
          },
          {
            quote: '"زيارتي لهم دايم تكون كأنها هروب صغير من الزحمة,  تعاملهم وأجواء المكان يرفعون معنوياتي"',
            author: 'ريم م.'
          }
        ] : [
          {
            quote: '"I never imagined beauty could feel this personal, this restorative."',
            author: 'Nada F.'
          },
          {
            quote: '"My face changed so much. I feel confident without makeup for the first time in years."',
            author: 'Haifa S.'
          },
          {
            quote: '"Every visit is a retreat. The personalized care and the atmosphere made me feel so valued."',
            author: 'Reem M.'
          }
        ]}
        onOpenSlidingForm={handleOpenSlidingForm}
      />

      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        promoBarText={navbarSectionPageData?.promoBarText}
        // promoBarVisibility={navbarSectionPageData?.promoBarVisibility}
        promoBarVisibility={false}
        onJoinRiviveClick={handleOpenSlidingForm}
        isLandingPage={true}
      />


      <div
        ref={shadowRef}
        className="absolute w-full aspect-3/4 tb:aspect-3/2 z-0 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          top: '0px',
          transform: isTb ? 'translateY(-50%)' : '',
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      />


      {/* <OverviewSection
        serviceCategorySectionPageData={serviceCategorySectionPageData}
        language={language}

      /> */}
      <UGCBenefitsSection
        id="ugc-benefits"
        language={language}
        subheadingText={language === 'arabic' ? "لماذا تختار النساء ريڤيڤ دون غيرها" : "Why women choose Rivive over everyone else"}
        values={language === 'arabic' ? [
          {
            heading: 'نتائج فورية من الجلسة الأولى',
            paragraph: '"كنت بالبداية أحسب إن العلاجات غير الجراحية ما تعطي نتيجة قوية، بس بعد كم جلسة تفاجأت من النتائج بدون أي ألم."',
            author: 'ساره ف.',
            muxPlaybackId: 'jqCQ00fvUM7W602uMJfeqfw4Wrq3aAYv2mOxq2P00v6EIw',
          },
          {
            heading: 'شخصي، خالٍ من الألم وغير جراحي',
            paragraph: '"توقعت أحتاج فترة تعافي واجازه من الدوام، بس طلع الموضوع أسهل من السهل, رحت عالعشا وأنا شكلي وإحساسي مرّة حلو."',
            author: 'نهى م.',
            muxPlaybackId: 'oP5t00012riOMkaHwyHjUjQ00JvK8lyxLu2DjDCwsrRVHY',
          },
          {
            heading: 'سهل وبدون أي فترة تعافي',
            paragraph: '"كنت بصراحة شاكة بموضوع إن كل شيء يكون مُفصَّل لي، بس تفاجأت إن ريڤيڤ سوّوا ثلاث تحاليل وصمّموا البرنامج كامل لي أنا"',
            author: 'لينا س.',
            muxPlaybackId: '6daqcUjNnpWx3uoPgJ7J00Gou00HuzQgNP00G2CxELp7Do',
          },
        ] : [
          {
            heading: 'Immediate results from the first session',
            paragraph: '”At first I thought non-invasive meant less effective, but after a few sessions I was amazed I could get such incredible results without any discomfort.”',
            author: 'Sarah F.',
            muxPlaybackId: 'jqCQ00fvUM7W602uMJfeqfw4Wrq3aAYv2mOxq2P00v6EIw',
          },
          {
            heading: 'Personalized, pain-free & non-invasive',
            paragraph: '”I was planning to take time off work after the treatment, but I actually never required any recovery. I went straight to dinner looking and feeling amazing.”',
            author: 'Nuha M.',
            muxPlaybackId: 'oP5t00012riOMkaHwyHjUjQ00JvK8lyxLu2DjDCwsrRVHY',
          },
          {
            heading: 'Effortless & zero recovery time',
            paragraph: '”I was skeptical about the ‘personalized’ claims, but Rivive actually did three analyses and designed everything specifically for me.”',
            author: 'Leena S.',
            muxPlaybackId: '6daqcUjNnpWx3uoPgJ7J00Gou00HuzQgNP00G2CxELp7Do',
          },
        ]}
        buttonText={language === 'arabic' ? "احجز استشارتك المجانية" : "Book your free consultation"}
        testimonial={language === 'arabic' ? {
          quote: 'تستحق كل دقيقة قضيتها في قائمة الانتظار',
          author: 'شهد ب.',
        } : {
          quote: '"Worth every minute on the waiting list."',
          author: 'Shahad B.',
        }}
        onOpenSlidingForm={handleOpenSlidingForm}
      />

      <LPServicesSection
        id="services"
        servicesSectionPageData={servicesSectionPageData}
        headlineText={language === 'arabic' ? "اكتشفوا علاجاتنا" : "Explore our treatments"}
        subheadingText={language === 'arabic' ? "الدقة تلتقي بالعناية" : "PRECISION MEETS CARE"}
        language={language}

        onInitialPosition={(pos) => {
          lpServicesPositionRef.current = pos;
          animateBg();
        }}
        buttonText={language === 'arabic' ? "احجز جلستك الاولى" : "Book your first session"}
        testimonial={language === 'arabic' ? {
          quote: '"تستحق كل دقيقة قضيتها في قائمة الانتظار"',
          author: 'شهد ب.'
        } : {
          quote: '"Worth every minute on the waiting list."',
          author: 'Shahad B.'
        }}
        onOpenSlidingForm={handleOpenSlidingForm}
      />



      {/* <LPBenefitsSection
        id="benefits"
        language={language}
        subheadingText={language === 'arabic' ? "لماذا تختار النساء ريڤيڤ دون غيرها" : "Why women choose Rivive over everyone else"}
        paragraphText={language === 'arabic' ? "في ريڤيڤ، نستخدم تقنيات البيوفيدباك المتقدمة وعلاجات شخصية غير جراحية لإبراز جمالك الطبيعي الحقيقي." : "At Rivive, we use advanced biofeedback technology and personalized, non-invasive treatments to reveal your true natural beauty."}
        values={benefitsSectionPageData.values}

        buttonText={language === 'arabic' ? "احجز استشارتك المجانية" : "Book your free consultation"}
        onOpenSlidingForm={handleOpenSlidingForm}
      /> */}
      {/* <LPForm
        language={language}
        headlineText="Begin your personalized beauty journey"
        subheadingText="LIMITED ACCESS"
        buttonText="Join Rivive"
        hintText="*20% Discount for Online Sign-ups"
      /> */}

      <LPResultsSection
        id="results"
        language={language}
        headlineText={language === 'arabic' ? "تحولات خالية من الألم وسهلة" : "Painless & effortless transformations"}
        subheadingText={language === 'arabic' ? "نتائج حقيقية" : "REAL RESULTS"}

        lpResultsSectionPageData={lpResultsSectionPageData}
      />


      {/* <LPImageSection
        language={language}
        imageSrc="/3_Landing-Page/Landing-3-Founding/Landing-Founding_.webp"
        imageAlt="Founding Members"
        heading={language === 'arabic' ? "امتيازات مدى الحياة مخصصة حصريًا للأعضاء المؤسسين" : "Lifetime privileges reserved for Founding Members"}
        paragraph={language === 'arabic' ? `تبقّت فقط ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN} عضويات تأسيسية من أصل ${FOUNDING_MEMBERSHIPS_TOTAL}. بادر بالتقديم لتصبح جزءًا من قصة انطلاق ريڤيڤ, وتنعم بامتيازاتٍ استثنائية لن تتكرر` : `Just ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN}/${FOUNDING_MEMBERSHIPS_TOTAL} Founding Memberships remain. Apply and become part of Rivive's founding story, unlocking privileges never to be offered again.`}
        buttonText={language === 'arabic' ? "انضم إلى المؤسسين الأوائل" : "Become a founder"}
        hintText={language === 'arabic' ? "لأعضاء الباقه البريميوم/VIP يرجى الالتزام بعد التقييم" : "*Premium/VIP commitment required post-evaluation."}
        id="image-section"

        onInitialPosition={(pos) => {
          lpImagePositionRef.current = pos;
          animateBg();
        }}
        progress={Math.round((FOUNDING_MEMBERSHIPS_TAKEN / FOUNDING_MEMBERSHIPS_TOTAL) * 100)}
        onOpenSlidingForm={handleOpenSlidingForm}
      /> */}




      <LPImageFormSection
        id="image-form"
        language={language}
        heading={language === 'arabic' ? "رحلتك نحو الجمال الطبيعي تبدأ من هنا" : "Your journey to natural beauty begins here"}
        // subheading="LIMITED ACCESS"
        paragraph={language === 'arabic' ? "لضمان أعلى مستوى من العناية، نقوم بحجز عدد محدود من المواعيد يوميًا. تبدأ رحلتك الحصرية معنا بانضمامك إلى قائمة الانتظار." : "To ensure the highest level of care, we book only a limited number of appointments daily. Your exclusive journey with us starts with you joining the waiting list."}
        benefit1={language === 'arabic' ? "بدون إبر أو جراحة" : "Without needles or surgery"}
        benefit2={language === 'arabic' ? "بدون الم" : "Without pain"}
        benefit3={language === 'arabic' ? "بدون فترة تعافي" : "Without recovery time"}
        backgroundImage="/3_Landing-Page/Landing-6-Image-Form/Landing-ImageForm.webp"
        shouldAnimate={false}
      />

      <LPDevicesSection
        id="devices"
        beautyDevicesSectionPageData={beautyDevicesSectionPageData}
        headlineText={language === 'arabic' ? "أجهزتنا الحصرية بالمملكة العربية السعوديه" : "Our KSA-exclusive devices"}
        subheadingText={language === 'arabic' ? "العلم يلتقي بالجمال" : "SCIENCE MEETS BEAUTY"}
        language={language}
        buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
        onOpenSlidingForm={handleOpenSlidingForm}
      />

      <LPAboutSection
        id="about"
        language={language}
        headlineText={language === 'arabic' ? "ما الذي يحفزنا" : "What drives us"}
        subheadingText={language === 'arabic' ? "تعرفوا علينا" : "GET TO KNOW US"}
        paragraphText={language === 'arabic' ? "في ريڤيڤ، نهدف إلى إرشاد الأشخاص في رحلات تحويلية يحتفى فيها بالجمال الأصيل، مع إعطاء الأولوية للرفاهية الطويلة الأمد على الحلول السريعة المؤقتة." : "At Rivive, we are on a mission to guide people on transformative journeys where authentic beauty is celebrated and long-lasting well-being is prioritized over temporary quick fixes."}
        paragraphText2={language === 'arabic' ? "يمتد هذا الالتزام إلى دعم رؤية السعودية 2030، حيث نقود ابتكار حلول تقنية جمالية مبتكرة توفر بدائل أكثر صحة ووعي للعناية التقليدية بالجمال. ومن خلال ذلك، نحتفي برؤية المملكة للابتكار، الرفاهية، والتعبير الأصيل عن الذات." : "This commitment extends to supporting Saudi Vision 2030, as we pioneer innovative beauty-tech solutions that offer healthier, more conscious alternatives to conventional beauty care. In doing so, we embrace the Kingdom's vision for innovation, wellness, and authentic self-expression."}
        imageSrc="/3_Landing-Page/Landing-7-About/Landing-About.webp"
        imageAlt="About Rivive"

      />

      <TeamSection
        id="team"
        teamMemberSectionPageData={teamMemberSectionPageData}
        headlineText={language === 'arabic' ? "خبراء عالميون مكرسون من أجلك" : "Global experts dedicated to you"}
        subheadingText={language === 'arabic' ? "تعرفوا على الفريق" : "MEET THE TEAM"}
        paragraphText={language === 'arabic' ? "يمزج خبراؤنا العالميون بين الإتقان التقني والضيافة الدافئة لإبراز جمالك الأصيل — كل ذلك من خلال تجربة شخصية، سرية، وتحويلية." : "Our global experts blend technical mastery with warm hospitality to reveal your authentic beauty — all through a personal, discreet, and transformative experience."}
        language={language}
        // onInitialPosition={(pos) => {
        //   // teamPositionRef.current = pos;
        //   // animateBg();
        // }}
        shownPercentage={isTb ? 0.5 : 0.2}
        animateHeadline={false}
        isLandingPage={true}
      />

      <LocationSection
        id="location"
        showHeadline={false}
        branchInfoSectionPageData={branchInfoSectionPageData}
        language={language}
        onInitialPosition={(pos) => {
          locationPositionRef.current = pos;
          // animateBg();
        }}
        hideSecondImage={!isLg}
        shouldAnimate={false}
        isLandingPage={true}
        onOpenSlidingForm={handleOpenSlidingForm}
      />

      <FAQSection
        language={language}
        questions={faqSectionPageData.questions || []}
        animateHeadline={false}
        shouldAnimate={false}
        subheading={language === 'arabic' ? "قرارات جمالية واعية" : "INFORMED BEAUTY DECISIONS"}
        heading={language === 'arabic' ? "الأسئلة الشائعة" : "Frequently asked questions"}
        paragraph={language === 'arabic' ? "كل ما تحتاجون معرفته عن علاجات ريڤيڤ الشخصية، وتقنياتها المتقدمة، وتجربتها الحصرية." : "Everything you need to know about Rivive's personalized treatments, advanced technology, and exclusive experience."}
      />

      <LPCtaFormSection
        id="cta-form-section"
        language={language}
        headlineText={language === 'arabic' ? "رحلتك نحو الجمال الطبيعي تبدأ من هنا" : "Your journey to natural beauty begins here"}
        subheadingText={language === 'arabic' ? "الوصول الحصري" : "EXCLUSIVE ACCESS"}
        paragraphText={language === 'arabic' ? "لضمان أعلى مستوى من العناية، نقوم بحجز عدد محدود من المواعيد يوميًا. تبدأ رحلتك الحصرية معنا بانضمامك إلى قائمة الانتظار." : "To ensure the highest level of care, we book only a limited number of appointments daily. Your exclusive journey with us starts with you joining the waiting list."}
        benefits={language === 'arabic' ? [
          "بدون إبر أو جراحة",
          "بدون الم",
          "بدون فترة تعافي"
        ] : [
          "Without needles or surgery",
          "Without pain",
          "Without recovery time"
        ]}
        formHeadlineText={language === 'arabic' ? "أبدئوا رحلتكم اليوم" : "Begin your transformation today"}
        formSubheadingText={language === 'arabic' ? "وصول محدود" : "LIMITED ACCESS"}
        onFormSubmit={(formData) => {
          debug.log('CTA Form submitted:', formData);
          // Handle form submission
        }}

      />

      {/* TODO: animations dont work atm here */}
      <LPFooterSection
        // brandInfoSectionPageData={brandInfoSectionPageData}
        language={language}
        setLanguage={setLanguage}

      />

      {/* Sliding Form Section */}
      <SlidingFormSection
        isOpen={isSlidingFormOpen}
        onClose={() => setIsSlidingFormOpen(false)}
        language={language}
        headlineText={language === 'arabic' ? "أبدئوا رحلتكم" : "Begin your journey"}
        subheadingText={language === 'arabic' ? "نقوم بحجز عدد محدود من المواعيد يوميًا لضمان أعلى مستوى من العناية." : "We book limited daily appointments to ensure the highest level of care."}
        benefits={language === 'arabic' ? [
          "بدون إبر أو جراحة",
          "بدون الم",
          "بدون فترة تعافي"
        ] : [
          "Without needles or surgery",
          "Without pain",
          "Without recovery time"
        ]}
      />
    </div>
  )
}

export default LandingPageClient;
